import { X } from 'lucide-react'

import { TOUCH_TARGET_Y } from '@/components/ui/button-variants'
import { cn } from '@/lib/utils'

export interface AppliedTag {
  readonly id: string
  /** The filter's name, in the quieter ink: "P/E", "Sector". */
  readonly name: string
  /** What it is set to: "5 – 15", "Hydropower". */
  readonly value: string
}

/**
 * The filters in force, one tag each (the mock's `nS`): a press on a tag takes that filter off.
 * The accent's soft fill under its hairline, the name quieter than the value, the × last.
 */
export default function AppliedFilters({
  tags,
  onRemove,
  className,
}: {
  tags: readonly AppliedTag[]
  onRemove: (id: string) => void
  className?: string
}) {
  if (tags.length === 0) return null
  return (
    <ul aria-label="Filters on" className={cn('flex flex-wrap items-center gap-2', className)}>
      {tags.map((tag) => (
        <li key={tag.id}>
          <button
            type="button"
            aria-label={`Remove ${tag.name}: ${tag.value}`}
            onClick={() => {
              onRemove(tag.id)
            }}
            className={cn(
              'group/tag relative inline-flex h-7 cursor-pointer items-center gap-2 border border-border-accent bg-tone-accent-soft pr-2 pl-3 text-body-xs text-accent-soft-foreground',
              'transition-colors duration-fast hover:border-accent',
              // The mock's 28px tag, grown to a 44px target on a touch screen.
              TOUCH_TARGET_Y,
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
            )}
          >
            <span>
              <span className="text-muted-foreground-subtle">{tag.name}</span> {tag.value}
            </span>
            <X
              aria-hidden="true"
              className="size-3 opacity-70 transition-opacity group-hover/tag:opacity-100"
            />
          </button>
        </li>
      ))}
    </ul>
  )
}
